import { z } from "zod";

import type { AuthMethod, DeviceSession, IdentityApi } from "../types";
import type { LiveCtx } from "./auth";
import { ROUTES } from "./routes";

type SessionsApi = Pick<IdentityApi, "listSessions" | "revokeSession" | "revokeAllSessions">;

/** The sign-in methods the service names; anything else reads as an email code, as the session does. */
const METHODS: readonly AuthMethod[] = ["email_code", "passkey", "google", "microsoft"];

const sessionInfo = z.object({
  id: z.string(),
  current: z.boolean(),
  userAgent: z.string().nullish(),
  ipAddress: z.string().nullish(),
  authMethod: z.string(),
  signedInAt: z.string(),
  lastActiveAt: z.string().nullish(),
  expiresAt: z.string(),
});

/** The device and browser a user agent names, roughly; enough for the Sessions and devices card. */
export function describeAgent(agent: string | undefined): { device: string; browser: string } {
  const text = agent ?? "";
  const device = /iPhone|iPad/.test(text)
    ? "iOS"
    : /Android/.test(text)
      ? "Android"
      : /Mac OS X|Macintosh/.test(text)
        ? "Mac"
        : /Windows/.test(text)
          ? "Windows"
          : /Linux/.test(text)
            ? "Linux"
            : "Unknown device";
  const browser = /Edg\//.test(text)
    ? "Edge"
    : /Firefox\//.test(text)
      ? "Firefox"
      : /Chrome\//.test(text)
        ? "Chrome"
        : /Safari\//.test(text)
          ? "Safari"
          : "Browser";
  return { device, browser };
}

export const parseSessions = z.array(sessionInfo).transform((list): DeviceSession[] =>
  list.map((item) => ({
    id: item.id,
    current: item.current,
    ...describeAgent(item.userAgent ?? undefined),
    city: "",
    ipAddress: item.ipAddress ?? "",
    authMethod: METHODS.find((method) => method === item.authMethod) ?? "email_code",
    createdAt: item.signedInAt,
    lastActiveAt: item.lastActiveAt ?? item.signedInAt,
  })),
);

/**
 * Sessions and devices (FR-ADMIN-048), live from WorkOS through the identity service: list, sign one out, and sign
 * out everywhere (this session included, as the mock does).
 */
export function createSessionsApi(ctx: LiveCtx): SessionsApi {
  const { http } = ctx;
  return {
    listSessions: () => http.get(ROUTES.sessions, parseSessions),
    revokeSession: (sessionId) => http.sendOnly("DELETE", ROUTES.session(sessionId), undefined),
    revokeAllSessions: () => http.sendOnly("DELETE", ROUTES.sessions, undefined),
  };
}
